import React from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import Theme from 'components/common/Theme/Theme'
import { CompactVehicleImg, VehicleYMME, CompactTrailerImage } from 'components/MyGarage2.0/components/SharedStyles'
import Link, { A } from 'components/common/Link/Link'
import { formatPhoneNumber } from 'helpers/phone-number'
import Icon from 'components/common/Icon/Icon'
import QuotedItemsAndServicesList from './components/QuotedItemsAndServicesList'
import Media from 'components/common/Media'

const AppointmentItem = ({
  appointmentDate,
  className,
  userVehicle: {
    tireFrontSize,
    vehicleNickname,
    masterVehicle: {
      modelDesc,
      makeDesc,
      yearDesc,
      subModelDesc
    },
    vehicleColor
  },
  nextAppointment: {
    appliedPromotions,
    quoteSummary,
    services,
    products,
    freeAligmentTooltip,
    storeAddress: {
      address1,
      city,
      state,
      zipcode,
      phoneNumber
    }
  },
  formattedVehicleNickName,
  formattedVehicleYMME,
  showUpdateCancelInstructionsModalHandler,
  locationPhoneNumberFormatted,
  googleMapsDirections,
  isDetailsAreExpanded,
  toggleAppointmentDetails,
  isGenericTrailer,
  isTrailer
}) =>
  <Wrapper className={ className }>
    <Date>
      {appointmentDate}
    </Date>
    <Vehicle>
      <VehicleImageWrapper>
        {(isTrailer || isGenericTrailer)
          ? <CompactTrailerImage asset="icon-trailer" alt={ 'Trailer' } />
          : (
            <VehicleImage
              year={ parseInt(yearDesc, 10) }
              make={ makeDesc }
              model={ modelDesc }
              submodel={ subModelDesc }
              color={ vehicleColor ? `#${vehicleColor}` : undefined } />
          )
        }
      </VehicleImageWrapper>

      <VehicleInfo>
        {isGenericTrailer
          ? <VehicleNickname>{vehicleNickname}</VehicleNickname>
          : <VehicleNickname>{formattedVehicleNickName}</VehicleNickname>
        }

        {(!isGenericTrailer && !isTrailer) && (
          <FormattedYMME>{formattedVehicleYMME}</FormattedYMME>
        )}
        {isTrailer && (
          <FormattedYMME>{tireFrontSize}</FormattedYMME>
        )}
        <LineLink
          href="#"
          data-at-appointment-editappointment
          aria-label="Update/Cancel Appointment"
          onClick={ showUpdateCancelInstructionsModalHandler } >
          Update/Cancel Appointment
        </LineLink>
      </VehicleInfo>
    </Vehicle>

    <StoreAddressWrapper>
      <StoreAddress>
        <AddressTitle>Bring your vehicle to</AddressTitle>
        <Address>
          { address1 }<br />
          { city }, { state } { zipcode }
        </Address>
        <Media.Desktop>
          <Phone
            data-at-store-phone
            href={ `tel:+${locationPhoneNumberFormatted}` }
            title="Telephone number">
            { formatPhoneNumber(locationPhoneNumberFormatted) }
          </Phone>
        </Media.Desktop>

      </StoreAddress>

      <DirectionsWrapper>
        <DirectionsLink
          data-at-appointment-viewstoredirections
          to={ googleMapsDirections } >
          Get Directions
        </DirectionsLink>
        <Media.Mobile>
          <Phone
            data-at-store-phone
            href={ `tel:+${locationPhoneNumberFormatted}` }
            title="Telephone number">
            { formatPhoneNumber(locationPhoneNumberFormatted) }
          </Phone>
        </Media.Mobile>
      </DirectionsWrapper>
    </StoreAddressWrapper>

    <ViewDetails>
      <ViewDetailsHeader>
        <ViewDetailsTitle>
          Quoted items & Services
        </ViewDetailsTitle>

        <Media.Desktop>
          <ViewDetailsButton onClick={ toggleAppointmentDetails }>
            View Details
            <ArrowIcon asset="arrow-down-common-red" isDetailsAreExpanded={ isDetailsAreExpanded } />
          </ViewDetailsButton>
        </Media.Desktop>
        <Media.Mobile>
          <ViewDetailsButton onClick={ toggleAppointmentDetails }>
            <ArrowIcon asset="arrow-down-common-red" isDetailsAreExpanded={ isDetailsAreExpanded } />
          </ViewDetailsButton>
        </Media.Mobile>
      </ViewDetailsHeader>

      <QuotedList
        phoneNumber={ phoneNumber }
        appliedPromotions={ appliedPromotions }
        quoteSummary={ quoteSummary }
        freeAligmentTooltip={ freeAligmentTooltip }
        isDetailsAreExpanded={ isDetailsAreExpanded }
        services={ services }
        products={ products } />
    </ViewDetails>
  </Wrapper>

AppointmentItem.propTypes = {
  appointmentDate: PropTypes.string,
  className: PropTypes.string,
  formattedVehicleNickName: PropTypes.string,
  formattedVehicleYMME: PropTypes.string,
  googleMapsDirections: PropTypes.string,
  isDetailsAreExpanded: PropTypes.bool,
  isGenericTrailer: PropTypes.bool,
  isTrailer: PropTypes.bool,
  locationPhoneNumberFormatted: PropTypes.string,
  nextAppointment: PropTypes.shape(),
  showUpdateCancelInstructionsModalHandler: PropTypes.func,
  toggleAppointmentDetails: PropTypes.func,
  userVehicle: PropTypes.shape()
}

export default AppointmentItem

const Wrapper = styled.div`
  width: 100%;
  padding: 0 6px 0 0;
`

const Date = styled.p`
  margin: 0 0 16px;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 700;
  color: ${Theme.colors.bright_red_new};
  transform: scale(1, 1.3);

  @media (max-width: 500px) {
    font-size: 14px;
  }
`

const Vehicle = styled.div`
  display: flex;
  width: 100%;
  margin: 0 0 16px;
`

const VehicleImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  max-width: 80px;
  max-height: 80px;
  width: 100%;
  background-color: ${Theme.colors.white_smoke};
`

const VehicleImage = styled(CompactVehicleImg)`
  max-width: unset;
  width: 70px;
  height: auto;
  object-fit: contain;
`

const VehicleInfo = styled.div`
  width: 100%;
  margin: 0 0 0 16px;
`

const VehicleNickname = styled.p`
  margin: 0 0 8px;
  text-transform: uppercase;
  word-break: break-word;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
  transform: scale(1, 1.3);

  @media (max-width: 500px) {
    font-size: 12px;
  }
`

const FormattedYMME = styled(VehicleYMME)`
  margin: 0 0 8px 0;

  @media (max-width: 500px) {
    font-size: 12px;
  }
`

const DirectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > a {
    margin: 0 0 5px 0;
  }
`

const DirectionsLink = styled(Link)`
  display: inline-block;
  text-transform: uppercase;
  font-weight: 700;
  line-height: 1.2;
  transform: scale(1, 1.3);

  @media (max-width: 500px) {
    font-size: 12px;
  }
`

const LineLink = styled.a`
  display: inline-block;
  text-transform: uppercase;
  text-decoration: none;
  font-weight: 700;
  line-height: 1.2;
  transform: scale(1, 1.3);
  color: ${Theme.colors.blue};

  @media (max-width: 500px) {
    font-size: 12px;
  }
`

const StoreAddressWrapper = styled.div`
  margin: 0 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;

  @media (max-width: 500px) {
    align-items: flex-start;
  }
`

const StoreAddress = styled.div`
  width: 100%;
  max-width: 140px;
`

const AddressTitle = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 700;
`

const Address = styled.p`
  margin: 0 0 8px;
  color: ${Theme.colors.medium_grey};
  font-size: 13px;
  letter-spacing: 1.2;
`

const Phone = styled(A)`
  text-decoration: none;
  font-weight: 700;
  color: ${Theme.colors.medium_grey};

  @media (max-width: 1023px) {
    color: ${Theme.colors.blue};
  }
`

const ViewDetails = styled.div`
  padding: 12px 16px;
  background-color: ${Theme.colors.white_smoke};
`

const ViewDetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const ViewDetailsTitle = styled.p`
  margin: 0;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 700;
  transform: scale(1, 1.3);

  @media (max-width: 1023px) {
    font-size: 12px;
  }
`

const ViewDetailsButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 700;
  color: ${Theme.colors.bright_red_new};
  background: none;
  border: 0;
  transform: scale(1, 1.3);
`

const ArrowIcon = styled(Icon)`
  margin: 0 0 0 4px;
  height: 7px;
  transition: transform 300ms ease-in-out;
  ${props => props.isDetailsAreExpanded && css`
    transform: rotate(180deg);
  `}
`

const QuotedList = styled(QuotedItemsAndServicesList)`
  overflow: hidden;
  margin: 0;
  max-height: 0;
  height: 0;
  transition: all 300ms ease-in-out;
  ${props => props.isDetailsAreExpanded && css`
    overflow: auto;
    height: auto;
    max-height: 1000vh;
  `}
`
